import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Bell, HelpCircle } from 'lucide-react';
import { resolveRoleUi } from '@efacture/core/sector';
import { toast } from 'sonner';
import type { ResolvedProfile } from '@efacture/core/sector';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Sidebar, MobileMenuButton } from './Sidebar';
import { TenantSwitcher } from './TenantSwitcher';
import { FirmContextBanner } from './FirmContextBanner';
import { TenantMismatchOverlay } from './TenantMismatchOverlay';
import { Button } from '@/components/ui/button';
import { NLPSearchBar } from '../nlp/NLPSearchBar';
import { NLPChatProvider, useNLPChat } from '../nlp/NLPChatContext';
import { PasskeySetupModal } from '../auth/PasskeySetupModal';
import { usePasskeyList } from '../../api/queries/passkey';
import { useSectorProfile } from '@/api/queries/sector-profile';
import { surfacesOf, useJurisdiction } from '@/api/queries/jurisdiction';
import { useNavHiddenWriter, useNavPreferences } from '@/api/queries/ui-preferences';
import { roleUiCodes } from '@/lib/role-ui-codes';
import { setTerminologyOverlay, clearTerminologyOverlay, USER_GUIDE_PATH, useTranslation } from '@/lib/i18n';
import { useUnreadCount } from '@/api/queries/notifications';
import { useNotificationStream } from '@/hooks/useNotificationStream';
import { NotificationPanel } from './NotificationPanel';
import { PushNotificationBanner } from './PushNotificationBanner';
import { PWAInstallBanner } from './PWAInstallBanner';
import { PWAUpdatePrompt } from './PWAUpdatePrompt';
import { NetworkStatusBanner } from './NetworkStatusBanner';
import { McfConnexionBanner } from '@efacture/web-billing/components/mcf/McfConnexionBanner';
import { McfOutageBanner } from '@efacture/web-billing/components/mcf/McfOutageBanner';
import { McfSignataireBanner } from '@efacture/web-billing/components/mcf/McfSignataireBanner';
import { TrialBanner } from '@/components/billing/TrialBanner';
import { LicenceBanner } from './LicenceBanner';
import { SfeNotices } from '@efacture/web-billing/components/sfe/SfeNotices';
import { UpsellBanner } from '@/components/billing/UpsellBanner';
import { useOfflineCache } from '@/hooks/useOfflineCache';
import { PageErrorBoundary } from './PageErrorBoundary';
import { isDesktopApp } from '@/lib/desktop';

// ─────────────────────────────────────────────────────────────
// Header Component
// ─────────────────────────────────────────────────────────────

interface HeaderProps {
  onMobileMenuClick: () => void;
  mobileMenuOpen: boolean;
  sidebarCollapsed: boolean;
}

function Header({ onMobileMenuClick, mobileMenuOpen, sidebarCollapsed }: HeaderProps) {
  const { user } = useAuth();
  const { isPanelVisible } = useNLPChat();
  const { data: unreadData } = useUnreadCount();
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const unreadCount = unreadData?.count ?? 0;

  return (
    // Below `lg` the chat panel overlays the page instead of taking its
    // width: a 480px reservation leaves nothing, or less than nothing, of a
    // phone or tablet screen. The header sits right below the top banners
    // (network, client file); a banner already covers the top safe area, so
    // the header pads only the part of the inset left uncovered. Only its
    // left and right edges animate: a transition on `top` would slide it in
    // from behind the banners each time the layout mounts.
    <header
      className={cn(
        'fixed top-[var(--app-top-offset,0px)] bg-white/80 backdrop-blur-md border-b border-slate-200 z-30',
        'h-[calc(4rem+max(0px,env(safe-area-inset-top)-var(--app-top-offset,0px)))]',
        'pt-[max(0px,env(safe-area-inset-top)-var(--app-top-offset,0px))]',
        'flex items-center justify-between gap-2 transition-[left,right] duration-300',
        'pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] lg:pl-6 lg:pr-6',
        sidebarCollapsed ? 'lg:left-[72px]' : 'lg:left-64',
        'left-0 right-0',
        isPanelVisible && 'lg:right-[480px]'
      )}
    >
      {/* Left Section */}
      <div className="flex items-center gap-3 min-w-0">
        <MobileMenuButton onClick={onMobileMenuClick} expanded={mobileMenuOpen} />
        {/* Sélecteur de dossier (portail cabinet) — ne s'affiche que si
            l'utilisateur a accès à plus d'un dossier. */}
        <TenantSwitcher />
      </div>

      {/* Center Section — Search */}
      <div className="flex-1 min-w-0 flex justify-center px-2 lg:px-4">
        <NLPSearchBar />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
        {/* Help Button — Download user guide */}
        <a href={USER_GUIDE_PATH} download>
          <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-700" title="Télécharger le guide utilisateur">
            <HelpCircle className="w-5 h-5" />
          </Button>
        </a>

        {/* Notifications: below `sm` the panel anchors to the header, full width */}
        <div className="sm:relative">
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-500 hover:text-slate-700 relative"
            onClick={() => setNotificationPanelOpen(!notificationPanelOpen)}
            aria-label="Notifications"
            aria-expanded={notificationPanelOpen}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
          <NotificationPanel
            open={notificationPanelOpen}
            onClose={() => setNotificationPanelOpen(false)}
          />
        </div>

        {/* User Avatar - Mobile */}
        <div className="lg:hidden w-8 h-8 rounded-full bg-gradient-to-br from-brand-lagune-700 to-brand-lagune-800 flex items-center justify-center text-white font-semibold text-sm">
          {user?.firstName?.charAt(0).toUpperCase() || 'U'}
        </div>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// Terminology overlay sync
// ─────────────────────────────────────────────────────────────

/**
 * Layers the tenant's sector vocabulary over the base i18n catalogue for
 * the lifetime of the resolved profile.
 *
 * Applied synchronously during render, not in an effect: an effect runs
 * after commit, so the render that first receives the resolved profile
 * — and everything under it, including the sidebar labels — would paint
 * with the base catalogue and only pick up the overlay on some later,
 * unrelated re-render. The write is a plain assignment derived only
 * from `profile`, so re-running it across a discarded render or a
 * double-invoked one (StrictMode) is harmless.
 *
 * An absent profile writes the EMPTY overlay rather than leaving the
 * previous one standing: switching tenants clears the query cache, so
 * the profile transits through `undefined` while this stays mounted, and
 * the outgoing dossier's vocabulary would otherwise survive the switch.
 * The effect clears the overlay on unmount, the other exit path.
 */
export function useTerminologyOverlaySync(profile: ResolvedProfile | null | undefined): void {
  setTerminologyOverlay(profile?.terminology ?? {});

  useEffect(() => {
    return () => clearTerminologyOverlay();
  }, []);
}

// ─────────────────────────────────────────────────────────────
// Inner Layout (needs context access)
// ─────────────────────────────────────────────────────────────

function AppLayoutInner() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  useNotificationStream();
  useOfflineCache();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Any route change closes the drawer, including one started outside it
  // (a link in the page, the command palette, back/forward).
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);
  const { isPanelVisible, openChat } = useNLPChat();
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  // The desktop shell never offers passkey setup, so it skips the lookup too.
  const offersPasskey = !isDesktopApp();
  const { data: passkeyData } = usePasskeyList({ enabled: offersPasskey });
  const { user } = useAuth();
  const [bannerHeight, setBannerHeight] = useState(0);
  const [firmBannerHeight, setFirmBannerHeight] = useState(0);
  // The client-file banner stacks under the network banner. Their sum is
  // published once, as `--app-top-offset` on the layout root: the header,
  // both sidebars and the page start below it.
  const topHeight = bannerHeight + firmBannerHeight;
  const topOffsetStyle = { '--app-top-offset': `${topHeight}px` } as CSSProperties;
  const { data: sectorProfileData } = useSectorProfile();
  useTerminologyOverlaySync(sectorProfileData?.profile ?? null);
  const { data: jurisdiction } = useJurisdiction();

  // Resolved here, not in the sidebar: `ProtectedRoute` has already
  // settled the session, so the roles are in hand on the first render and
  // the sidebar can seed its open sections from them.
  const roleUi = useMemo(() => resolveRoleUi(roleUiCodes(user?.roles ?? [])), [user?.roles]);

  // The company's switched-off entries and the user's hidden ones, resolved
  // here for the same reason as the sector profile.
  const { companyDisabled, userHidden } = useNavPreferences();
  const hiddenWriter = useNavHiddenWriter();
  const onWriteError = { onError: () => toast.error(t('nav.hideFailed')) };

  useEffect(() => {
    if (
      offersPasskey &&
      user &&
      !user.passkeyPromptDismissed &&
      passkeyData?.passkeys?.length === 0 &&
      window.PublicKeyCredential
    ) {
      setShowPasskeyPrompt(true);
    }
  }, [offersPasskey, user, passkeyData]);

  return (
    <div className="min-h-screen bg-slate-50" style={topOffsetStyle}>
      <NetworkStatusBanner onTopHeightChange={setBannerHeight} />
      <FirmContextBanner top={bannerHeight} onHeightChange={setFirmBannerHeight} />

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        profile={sectorProfileData?.profile ?? null}
        roleUi={roleUi}
        jurisdictionSurfaces={surfacesOf(jurisdiction)}
        companyDisabled={companyDisabled}
        userHidden={userHidden}
        onHideItem={(key) => hiddenWriter.hide(key, onWriteError)}
        onShowHidden={() => hiddenWriter.showAll(onWriteError)}
        onOpenChat={openChat}
      />

      {/* Header */}
      <Header
        onMobileMenuClick={() => setMobileMenuOpen(true)}
        mobileMenuOpen={mobileMenuOpen}
        sidebarCollapsed={sidebarCollapsed}
      />

      {/* Main Content */}
      <main
        className={cn(
          'min-h-screen transition-[padding-left,padding-right] duration-300',
          'pt-[calc(4rem+max(var(--app-top-offset,0px),env(safe-area-inset-top)))]',
          sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64',
          isPanelVisible && 'lg:pr-[480px]'
        )}
      >
        <div className="py-4 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pb-[max(1rem,env(safe-area-inset-bottom))] lg:p-6">
          {/* §2.31 / annex C2h warning: MCF machine with no connection
              to the Administration for more than 24 hours */}
          <McfConnexionBanner />
          {/* Arrêté n°2025-0049: open MCF outages and provisional documents past their 48 hours */}
          <McfOutageBanner />
          {/* #328: the établissement must certify and its signer does not */}
          <McfSignataireBanner />
          <LicenceBanner />
          <SfeNotices />
          {/* Bandeau d'essai (TRIALING) + bannière upsell 402/403 (P0.3) */}
          <TrialBanner />
          <UpsellBanner />
          <PageErrorBoundary resetKey={pathname}>
            <Outlet />
          </PageErrorBoundary>
        </div>
      </main>

      <PushNotificationBanner />
      <PWAInstallBanner />
      <PWAUpdatePrompt />

      {showPasskeyPrompt && (
        <PasskeySetupModal onClose={() => setShowPasskeyPrompt(false)} />
      )}

      <TenantMismatchOverlay />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// App Layout Component (wraps with context provider)
// ─────────────────────────────────────────────────────────────

export function AppLayout() {
  const { tenant } = useAuth();
  // Keyed on the active tenant: a change of tenant remounts the layout and
  // the page under it, so no component state (a POS cart, a form, the
  // assistant's thread) outlives the tenant it was built in.
  return (
    <NLPChatProvider key={tenant?.id ?? 'no-tenant'}>
      <AppLayoutInner />
    </NLPChatProvider>
  );
}

export default AppLayout;
